import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getRateHistory, getRatesAt, type RateCurrency } from '../../shared/api/exchangeRates.api'
import { EmptyState } from '../../shared/ui/EmptyState'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { Spinner } from '../../shared/ui/Spinner'
import { RateHistoryArchive } from './RateHistoryArchive'
import {
  CURRENCIES,
  CURRENCY_LABEL,
  formatDateTime,
  formatRate,
  RateHistoryTable,
} from './RateHistoryTable'

/** Сколько последних изменений видно сразу; остальное — в архиве. */
export const RECENT_LIMIT = 10
/** Изменений за один день больше не бывает; запас на ручные исправления. */
const DAY_LIMIT = 1000

/** Границы выбранного дня (YYYY-MM-DD) по местному времени браузера, ISO. */
function dayBounds(day: string): { from: string; to: string } | null {
  const start = new Date(`${day}T00:00:00`)
  if (Number.isNaN(start.getTime())) return null
  const end = new Date(start)
  end.setDate(end.getDate() + 1)
  return { from: start.toISOString(), to: new Date(end.getTime() - 1).toISOString() }
}

/**
 * История курсов (этап 3, кусок C2): каждое изменение курса — кто и когда.
 * Плюс «курс на момент»: какой курс действовал в выбранные дату и время —
 * за сутки курс может меняться несколько раз (нужно для постоплат и оплат
 * поставщикам траншами).
 *
 * Сразу видны последние 10 изменений, более ранние — в свёрнутом архиве (из базы
 * ничего не удаляется). Поиск по дате показывает все изменения за выбранный день
 * по всей истории, включая архив (просьба Сергея 16.10.2026).
 */
export function ExchangeRateHistory() {
  const [currency, setCurrency] = useState<RateCurrency | null>(null)
  const [atLocal, setAtLocal] = useState('')
  const [day, setDay] = useState('')
  const bounds = useMemo(() => (day ? dayBounds(day) : null), [day])

  const history = useQuery({
    queryKey: ['exchange-rate-history', currency, bounds?.from ?? null],
    queryFn: () =>
      getRateHistory({
        currency: currency ?? undefined,
        ...(bounds ? { ...bounds, limit: DAY_LIMIT } : { limit: RECENT_LIMIT }),
      }),
  })

  // datetime-local без часового пояса — трактуем как местное время браузера.
  // Берём конец выбранной минуты: таблица показывает время с точностью до минуты,
  // и курс, поставленный в 15:30:27, «на 15:30» должен уже действовать.
  const atIso = useMemo(() => {
    if (!atLocal) return null
    const d = new Date(atLocal)
    return Number.isNaN(d.getTime()) ? null : new Date(d.getTime() + 59_999).toISOString()
  }, [atLocal])

  const ratesAt = useQuery({
    queryKey: ['exchange-rates-at', atIso],
    queryFn: () => getRatesAt(atIso!),
    enabled: atIso !== null,
  })

  const rows = history.data?.items ?? []
  const dayLabel = bounds ? new Date(bounds.from).toLocaleDateString('ru-RU') : ''

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-base font-semibold text-gray-900">История курсов</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Каждое изменение курса — кто и когда его поставил. История ведётся с 23.09.2026.
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-2">
        <label className="block text-xs text-gray-500" htmlFor="rate-at-input">
          Курс на момент
        </label>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <input
            id="rate-at-input"
            type="datetime-local"
            value={atLocal}
            onChange={(e) => setAtLocal(e.target.value)}
            className="w-full sm:w-auto px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          {ratesAt.isFetching && <Spinner className="h-4 w-4" />}
          {ratesAt.data && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
              {CURRENCIES.map((c) => {
                const r = ratesAt.data[c]
                return (
                  <span key={c} className="text-gray-700">
                    <span className="text-gray-400">{CURRENCY_LABEL[c]}:</span>{' '}
                    {r ? (
                      <span title={`Установлен ${formatDateTime(r.setAt)}`}>
                        {formatRate(r.rate)}
                      </span>
                    ) : (
                      <span className="text-gray-400">не записан</span>
                    )}
                  </span>
                )
              })}
            </div>
          )}
        </div>
        {ratesAt.isError && <ErrorMessage error={ratesAt.error} />}
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Валюта">
        {[null, ...CURRENCIES].map((c) => (
          <button
            key={c ?? 'all'}
            type="button"
            onClick={() => setCurrency(c)}
            aria-pressed={currency === c}
            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
              currency === c
                ? 'bg-brand-600 text-white border-brand-600'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {c ? CURRENCY_LABEL[c] : 'Все'}
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <label className="text-sm text-gray-600" htmlFor="rate-day-search">
          Найти изменения за дату
        </label>
        <div className="flex items-center gap-2">
          <input
            id="rate-day-search"
            type="date"
            value={day}
            onChange={(e) => setDay(e.target.value)}
            className="w-full sm:w-auto px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          {day && (
            <button
              type="button"
              onClick={() => setDay('')}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              Сбросить
            </button>
          )}
        </div>
      </div>

      <h3 className="text-sm font-medium text-gray-700">
        {bounds ? `Изменения за ${dayLabel}` : 'Последние изменения'}
      </h3>

      {history.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner className="h-6 w-6" />
        </div>
      ) : history.isError ? (
        <ErrorMessage error={history.error} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={bounds ? `За ${dayLabel} курс не менялся` : 'Изменений курса пока нет'}
        />
      ) : (
        <RateHistoryTable
          rows={rows}
          label={bounds ? `Изменения курса за ${dayLabel}` : 'Последние изменения курса'}
        />
      )}

      {!bounds && history.data?.hasMore && (
        <RateHistoryArchive currency={currency} skip={RECENT_LIMIT} />
      )}
    </div>
  )
}
